import { NavLink } from "react-router-dom";
import {
  Compass, BookOpenText, Settings, Newspaper, History, X, LogOut,
  PenSquare, ShieldCheck, ChevronDown, Check, Send, Terminal, CalendarClock,
  Cpu, DollarSign, AlertTriangle,
  Upload, MessageCircle, Printer, FileJson, Megaphone, Mail,
  Sun, Moon, Bell, CalendarDays, BarChart3, Images, BookOpen, Inbox,
} from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { NAV } from "@/constants/testIds";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { useNotifications } from "@/lib/notifications";
import NotificationsCenter from "@/components/NotificationsCenter";
import HealthWidget from "@/components/HealthWidget";
import { toast } from "sonner";

// Library is first and is the page Newsdesk opens on ("/" redirects to
// it in App.js); Research lives at /research (2026-09-28).
const items = [
  { to: "/library", label: "Library", icon: BookOpenText, testId: NAV.library },
  { to: "/runs", label: "Past Runs", icon: History, testId: NAV.runs },
  { to: "/research", label: "Research", icon: Compass, testId: NAV.research },
  { to: "/custom", label: "Write custom article", icon: PenSquare, testId: NAV.custom },
  { to: "/upload", label: "Upload article", icon: Upload, testId: "nav-upload" },
  { to: "/calendar", label: "Content calendar", icon: CalendarDays, testId: "nav-content-calendar" },
  { to: "/social-comments", label: "Social comments", icon: MessageCircle, testId: "nav-social-comments" },
  { to: "/prospects", label: "Prospects", icon: Inbox, testId: "nav-prospects" },
  { to: "/newsletter", label: "Newsletter", icon: Mail, testId: "nav-newsletter" },
  { to: "/sponsor-ads", label: "Sponsor ads", icon: Megaphone, testId: "nav-sponsor-ads" },
  { to: "/print-edition", label: "Convert to print", icon: Printer, testId: "nav-print-edition" },
  { to: "/editions", label: "Digital editions", icon: BookOpen, testId: "nav-digital-editions" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testId: "nav-analytics" },
  { to: "/stock", label: "Newsdesk Stock", icon: Images, testId: "nav-stock" },
  { to: "/scheduled", label: "Scheduled tasks", icon: CalendarClock, testId: "nav-scheduled-tasks" },
  { to: "/telegram", label: "Telegram tips", icon: Send, testId: NAV.telegram },
  { to: "/settings", label: "Settings", icon: Settings, testId: NAV.settings },
];

export default function Sidebar({ isOpen, onClose }) {
  const { user, logout, switchProperty } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { unread, markAllRead } = useNotifications();
  const [notifOpen, setNotifOpen] = useState(false);
  const properties = user?.properties || [];
  const activeId = user?.active_property_id;
  const activeProperty = properties.find((p) => p.id === activeId);
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const handleSwitch = async (propertyId) => {
    setSwitcherOpen(false);
    if (propertyId === activeId) return;
    try {
      await switchProperty(propertyId);
      toast.success("Property switched");
      // Full reload keeps every query cache scoped to the new property
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to switch property");
    }
  };

  const isAdmin = user?.role === "admin";

  // Prospects waiting for a reply (the menu badge). Every 2 minutes, the
  // same pace as the mailbox check; quiet when the request fails.
  const prospectsCounts = useQuery({
    queryKey: ["prospects-counts", ""],
    queryFn: () => api.prospectsCounts(""),
    enabled: !!activeProperty,
    refetchInterval: 120 * 1000,
    retry: false,
  });
  const prospectsWaiting = prospectsCounts.data?.needs_reply || 0;

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={onClose}
          aria-hidden="true"
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        className={[
          "w-60 shrink-0 border-r border-[var(--border)] bg-[var(--surface)] flex flex-col",
          "fixed inset-y-0 left-0 z-40 transition-transform duration-200 md:relative md:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
      >
        <div
          data-testid={NAV.logo}
          className="px-6 pt-6 pb-5 border-b border-[var(--border)] flex items-start justify-between"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Newspaper className="w-4 h-4 text-[var(--accent)]" strokeWidth={2} />
              <span className="overline">Newsdesk</span>
            </div>
            {/* Property name — switcher if the user has >1, plain label otherwise */}
            {properties.length > 1 ? (
              <div className="relative">
                <button
                  data-testid="property-switcher-toggle"
                  onClick={() => setSwitcherOpen((v) => !v)}
                  className="flex items-center gap-1.5 font-serif-display text-xl leading-tight tracking-tight text-left hover:text-[var(--accent)]"
                >
                  <span className="truncate">
                    {activeProperty?.name || "Select property"}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 shrink-0 transition-transform ${switcherOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {switcherOpen && (
                  <div
                    data-testid="property-switcher-menu"
                    className="absolute left-0 right-0 top-full mt-1 bg-[var(--surface)] border border-[var(--border)] shadow-md z-10"
                  >
                    {properties.map((p) => (
                      <button
                        key={p.id}
                        data-testid={`property-switcher-item-${p.slug}`}
                        onClick={() => handleSwitch(p.id)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--secondary)] text-left"
                      >
                        {p.id === activeId ? (
                          <Check className="w-3.5 h-3.5 text-[var(--accent)] shrink-0" />
                        ) : (
                          <span className="w-3.5 h-3.5 shrink-0" />
                        )}
                        <span className="truncate">{p.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="font-serif-display text-xl leading-tight tracking-tight truncate">
                {activeProperty?.name || (
                  <span className="text-[var(--ink-muted)] text-base italic">
                    No property assigned
                  </span>
                )}
              </div>
            )}
            <div className="text-xs text-[var(--ink-muted)] mt-1 font-mono-display">
              editorial desk
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close menu"
            className="md:hidden p-1 -mr-1 text-[var(--ink-muted)] hover:text-[var(--ink)]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {/* Editorial items require an active property */}
          {activeProperty &&
            items.map(({ to, label, icon: Icon, testId, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={onClose}
                data-testid={testId}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-3 py-2 mb-1 text-sm",
                    isActive
                      ? "bg-[var(--slate-900)] text-white"
                      : "text-[var(--ink)] hover:bg-[var(--secondary)]",
                  ].join(" ")
                }
              >
                <Icon className="w-4 h-4" strokeWidth={2} />
                <span>{label}</span>
                {to === "/prospects" && prospectsWaiting > 0 && (
                  <span
                    data-testid="nav-prospects-count"
                    className="ml-auto text-[10px] font-mono-display px-1.5 py-0.5 bg-[var(--accent)] text-white"
                  >
                    {prospectsWaiting}
                  </span>
                )}
              </NavLink>
            ))}

          {isAdmin && (
            <>
              <div className="mt-6 mb-2 px-3 overline text-[var(--ink-muted)]">
                Admin
              </div>
              <NavLink
                to="/admin"
                onClick={onClose}
                data-testid="nav-admin"
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-3 py-2 mb-1 text-sm",
                    isActive
                      ? "bg-[var(--slate-900)] text-white"
                      : "text-[var(--ink)] hover:bg-[var(--secondary)]",
                  ].join(" ")
                }
              >
                <ShieldCheck className="w-4 h-4" strokeWidth={2} />
                <span>Users &amp; Properties</span>
              </NavLink>
              <NavLink
                to="/admin/llm-models"
                onClick={onClose}
                data-testid="nav-admin-llm-models"
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-3 py-2 mb-1 text-sm",
                    isActive
                      ? "bg-[var(--slate-900)] text-white"
                      : "text-[var(--ink)] hover:bg-[var(--secondary)]",
                  ].join(" ")
                }
              >
                <Cpu className="w-4 h-4" strokeWidth={2} />
                <span>LLM Models</span>
              </NavLink>
              <NavLink
                to="/admin/cost"
                onClick={onClose}
                data-testid="nav-admin-llm-cost"
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-3 py-2 mb-1 text-sm",
                    isActive
                      ? "bg-[var(--slate-900)] text-white"
                      : "text-[var(--ink)] hover:bg-[var(--secondary)]",
                  ].join(" ")
                }
              >
                <DollarSign className="w-4 h-4" strokeWidth={2} />
                <span>LLM Cost</span>
              </NavLink>
              <NavLink
                to="/admin/request-log"
                onClick={onClose}
                data-testid="nav-admin-request-log"
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-3 py-2 mb-1 text-sm",
                    isActive
                      ? "bg-[var(--slate-900)] text-white"
                      : "text-[var(--ink)] hover:bg-[var(--secondary)]",
                  ].join(" ")
                }
              >
                <AlertTriangle className="w-4 h-4" strokeWidth={2} />
                <span>Request Log</span>
              </NavLink>
              <NavLink
                to="/admin/upload-json"
                onClick={onClose}
                data-testid="nav-admin-upload-json"
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-3 py-2 mb-1 text-sm",
                    isActive
                      ? "bg-[var(--slate-900)] text-white"
                      : "text-[var(--ink)] hover:bg-[var(--secondary)]",
                  ].join(" ")
                }
              >
                <FileJson className="w-4 h-4" strokeWidth={2} />
                <span>Upload JSON</span>
              </NavLink>
              <NavLink
                to="/prompts"
                onClick={onClose}
                data-testid="nav-prompts"
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-3 py-2 mb-1 text-sm",
                    isActive
                      ? "bg-[var(--slate-900)] text-white"
                      : "text-[var(--ink)] hover:bg-[var(--secondary)]",
                  ].join(" ")
                }
              >
                <Terminal className="w-4 h-4" strokeWidth={2} />
                <span>Prompt history</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="px-3 py-3 border-t border-[var(--border)]">
          {user && (
            <div className="px-3 pb-2 text-[11px] text-[var(--ink-muted)] font-mono-display truncate">
              {user.email}
              {user.role === "admin" && (
                <span className="ml-1 text-[var(--accent)]">· admin</span>
              )}
            </div>
          )}
          {/* Admin-only health widget — renders nothing for non-admins.
              Placed here so the badge sits directly under the username
              (per user request) and above the notifications button. */}
          <HealthWidget isAdmin={isAdmin} />
          <button
            data-testid="notifications-btn"
            onClick={() => {
              setNotifOpen(true);
              markAllRead();
            }}
            aria-label="Open notifications"
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-[var(--ink)] hover:bg-[var(--secondary)]"
          >
            <span className="relative flex items-center justify-center">
              <Bell className="w-4 h-4" />
              {unread > 0 && (
                <span
                  data-testid="notifications-unread-dot"
                  aria-label={`${unread} unread notifications`}
                  className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 flex items-center justify-center rounded-full bg-[var(--accent)] text-white text-[9px] font-mono-display font-semibold leading-none"
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </span>
            <span>Notifications</span>
          </button>
          <button
            data-testid="theme-toggle-btn"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-[var(--ink)] hover:bg-[var(--secondary)]"
          >
            {theme === "dark" ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
            <span>{theme === "dark" ? "Light mode" : "Dark mode"}</span>
          </button>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-[var(--ink)] hover:bg-[var(--secondary)]"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>
      <NotificationsCenter open={notifOpen} onOpenChange={setNotifOpen} />
    </>
  );
}
